import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { AIRAMark } from '@/components/brand/Marks';
import { CheckCircle, Circle, ArrowRight, Play, RotateCcw, Loader2 } from 'lucide-react';

const STAGE_ICONS = { completed: CheckCircle, in_progress: Play, pending: Circle };

export default function OnboardingPage() {
  const api = useApi();
  const [status, setStatus] = useState(null);
  const [stages, setStages] = useState([]);
  const [modes, setModes] = useState({});
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [startForm, setStartForm] = useState({ mga_name: '', calibration_mode: 'standard' });
  const [starting, setStarting] = useState(false);

  const load = async () => {
    try {
      const [statusRes, stagesRes] = await Promise.all([api.get('/onboarding/status'), api.get('/onboarding/stages')]);
      setStatus(statusRes.data);
      setStages(stagesRes.data.stages);
      setModes(stagesRes.data.calibration_modes);
    setLoading(false);
    } catch (err) { console.error("API error:", err?.message); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleStart = async () => {
    setStarting(true);
    try {
      await api.post('/onboarding/start', startForm);
      await load();
    setStarting(false);
    } catch (err) { console.error("API error:", err?.message); }
  };

  const handleAdvance = async () => {
    setAdvancing(true);
    try {
      await api.post('/onboarding/advance', { stage_data: {} });
      await load();
    setAdvancing(false);
    } catch (err) { console.error("API error:", err?.message); }
  };

  const handleReset = async () => {
    await api.post('/onboarding/reset');
    await load();
  };

  if (loading) return <div data-testid="onboarding-loading"><Skeleton className="h-64 rounded-sm" /></div>;

  const isStarted = status?.status === 'in_progress' || status?.status === 'completed';
  const stageList = status?.stages || stages;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="onboarding-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans', sans-serif" }}>MGA Onboarding</h1>
          <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>
            {isStarted ? `${status.mga_name} — Stage ${status.current_stage}/8` : 'Start a new MGA onboarding journey'}
          </p>
        </div>
        {isStarted && (
          <Button data-testid="reset-onboarding" variant="outline" size="sm" onClick={handleReset} className="rounded-sm text-xs">
            <RotateCcw className="w-3 h-3 mr-1" /> Reset
          </Button>
        )}
      </div>

      {!isStarted ? (
        <Card className="p-6 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-5">
          <div className="flex items-center gap-2">
            <AIRAMark size="text-xl" />
            <span className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Start New Onboarding</span>
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#5C6B7A' }}>MGA Name</Label>
            <Input data-testid="onboarding-mga-name" value={startForm.mga_name} onChange={e => setStartForm(p => ({...p, mga_name: e.target.value}))}
              className="mt-1 rounded-sm" placeholder="e.g. Narayan Insurance MGA" />
          </div>
          <div>
            <Label className="text-xs mb-2 block" style={{ color: '#5C6B7A' }}>Calibration Mode</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(modes).map(([key, mode]) => (
                <button key={key} data-testid={`mode-${key}`} onClick={() => setStartForm(p => ({...p, calibration_mode: key}))}
                  className={`p-4 rounded-sm border text-left transition-all ${startForm.calibration_mode === key ? 'border-[#00B4D8] bg-[rgba(0,180,216,0.05)]' : 'border-[rgba(136,150,168,0.2)] hover:border-[#1A4C7C]'}`}>
                  <p className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>{mode.name}</p>
                  <p className="text-xs mt-1" style={{ color: '#5C6B7A' }}>{mode.desc}</p>
                </button>
              ))}
            </div>
          </div>
          <Button data-testid="start-onboarding-btn" onClick={handleStart} disabled={!startForm.mga_name || starting}
            className="rounded-sm font-semibold" style={{ background: '#1A4C7C' }}>
            {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            Begin Onboarding
          </Button>
        </Card>
      ) : (
        <>
          {/* Progress */}
          <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>
                {status.calibration_info?.name || 'Fraime Standard'} Calibration
              </span>
              <span className="text-xs px-2 py-0.5 rounded-sm font-semibold capitalize"
                style={{ background: status.status === 'completed' ? 'rgba(29,158,117,0.1)' : 'rgba(0,180,216,0.1)', color: status.status === 'completed' ? '#1D9E75' : '#00B4D8' }}>
                {status.status}
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${((status.current_stage - 1) / 8) * 100}%`, background: '#00B4D8' }} />
            </div>
          </Card>

          {/* Stages */}
          <div className="space-y-2">
            {stageList.map((stage, idx) => {
              const Icon = STAGE_ICONS[stage.status] || Circle;
              const isActive = stage.status === 'in_progress';
              const isDone = stage.status === 'completed';
              return (
                <Card key={idx} data-testid={`stage-${stage.stage}`}
                  className={`p-4 rounded-sm border transition-all ${isActive ? 'border-[#00B4D8] bg-[rgba(0,180,216,0.03)]' : 'border-[rgba(136,150,168,0.2)] bg-white'}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0"
                      style={{ background: isDone ? 'rgba(29,158,117,0.1)' : isActive ? 'rgba(0,180,216,0.1)' : 'rgba(136,150,168,0.1)' }}>
                      <Icon className="w-4 h-4" style={{ color: isDone ? '#1D9E75' : isActive ? '#00B4D8' : '#5C6B7A' }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono" style={{ color: '#5C6B7A' }}>Stage {stage.stage}</span>
                        <span className="text-sm font-semibold" style={{ color: isDone ? '#1D9E75' : isActive ? '#0D1B2A' : '#5C6B7A' }}>{stage.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(136,150,168,0.1)', color: '#5C6B7A' }}>{stage.agent}</span>
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: '#5C6B7A' }}>{stage.desc}</p>
                    </div>
                    {isDone && stage.completed_at && (
                      <span className="text-[10px]" style={{ color: '#1D9E75' }}>{new Date(stage.completed_at).toLocaleString()}</span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {status.status !== 'completed' && (
            <Button data-testid="advance-stage-btn" onClick={handleAdvance} disabled={advancing}
              className="rounded-sm font-semibold" style={{ background: '#00B4D8' }}>
              {advancing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Complete Stage {status.current_stage} & Advance
            </Button>
          )}

          {status.status === 'completed' && (
            <Card className="p-6 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#1D9E75' }}>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6" style={{ color: '#1D9E75' }} />
                <div>
                  <h3 className="font-semibold" style={{ color: '#1D9E75' }}>Onboarding Complete</h3>
                  <p className="text-sm" style={{ color: '#5C6B7A' }}>{status.mga_name} is live on Fraime. AIRIS will now invite the broker panel.</p>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
